import {
  DndContext,
  DragOverlay,
  PointerSensor,
  closestCenter,
  pointerWithin,
  rectIntersection,
  useDroppable,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { gql } from "@apollo/client";
import { useMutation } from "@apollo/client/react";
import { useBackendQuery as useQuery } from "@/hooks/useBackendQuery";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { GripVerticalIcon, PlusIcon, TagsIcon, Trash2Icon } from "lucide-react";

import { TagColorEnum } from "@/__generated__/graphql";
import { Icon } from "@/components/atoms/Icon";
import { Popper } from "@/components/atoms/Popper";
import { useContextHotkeys, useContextScope } from "@/hooks/useContextTree";
import { CommandCenterListFooter } from "@/layouts/AppLayout/CommandCenter/CommandCenterListFooter";
import { Mutations } from "@/constants";
import { TagColors } from "@/constants/values";
import { icons } from "@/objects/@icons";
import { idToIdentifier } from "@/objects/@utils";
import { useColorScheme } from "@/hooks/useColorScheme";
import { useScope } from "@/hooks/useScope";
import { State } from "@/state";
import { Skeleton } from "@/components/ui/skeleton";
import { Typography, typographyStyles } from "@/components/ui/typography";
import { cn } from "@/components/ui/utils";

// The business's accounts - its categories among them, each under the account
// it narrows.
const CHARTED_ACCOUNTS_QUERY = gql`
  query ChartedAccountListForCategories($entityIdentifiers: [String!]) {
    chartedAccounts(entityIdentifiers: $entityIdentifiers) {
      data {
        id
        identifier
        path
        name
        isCategory
        color
        parent {
          id
        }
      }
    }
  }
`;

type CategoryItem = {
  id: string;
  identifier: string | null;
  name: string;
  color: TagColorEnum;
  position: number;
  chartedAccountId: string | null;
};

type ChartedAccountOption = {
  id: string;
  identifier: string;
  path: string;
  name: string;
  isCategory?: boolean | null;
  color?: TagColorEnum | null;
  parent?: { id: string } | null;
};

type TreeNode = {
  account: ChartedAccountOption;
  children: TreeNode[];
  depth: number;
};

const UNASSIGNED_GROUP = "__unassigned__";

const GRID_COLS = 4;

function buildTree(accounts: ChartedAccountOption[]): TreeNode[] {
  const sorted = [...accounts].sort((a, b) => a.path.localeCompare(b.path));
  const nodeMap = new Map<string, TreeNode>();
  const roots: TreeNode[] = [];

  for (const account of sorted) {
    const node: TreeNode = { account, children: [], depth: 0 };
    nodeMap.set(account.path, node);

    const segments = account.path.split(".").filter(Boolean);
    if (segments.length <= 1) {
      roots.push(node);
    } else {
      const parentPath = segments.slice(0, -1).join(".") + ".";
      const parent = nodeMap.get(parentPath);
      if (parent) {
        parent.children.push(node);
        node.depth = parent.depth + 1;
      } else {
        roots.push(node);
      }
    }
  }

  return roots;
}

function flattenVisibleTree(
  nodes: TreeNode[],
  expandedIds: Set<string>,
): TreeNode[] {
  const result: TreeNode[] = [];
  function walk(list: TreeNode[]) {
    for (const node of list) {
      result.push(node);
      if (node.children.length > 0 && expandedIds.has(node.account.id)) {
        walk(node.children);
      }
    }
  }
  walk(nodes);
  return result;
}

function ColorGrid({
  onChange,
  selectedColor,
}: {
  onChange: (color: TagColorEnum) => void;
  selectedColor?: TagColorEnum;
}) {
  const { colorScheme: mode } = useColorScheme();
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const entries = Object.entries(TagColorEnum);

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(${GRID_COLS}, 1fr)`,
        gap: "8px",
        padding: "8px",
        width: "fit-content",
      }}
    >
      {entries.map(([key, value], idx) => (
        <button
          key={key}
          ref={(el) => {
            itemRefs.current[idx] = el;
          }}
          onClick={() => onChange(value as TagColorEnum)}
          style={{
            width: 24,
            height: 24,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            border: selectedColor === value ? "2px solid" : "1px solid",
            padding: 0,
            background: "none",
            ...TagColors?.[value as TagColorEnum]?.[mode!],
          }}
        >
          A
        </button>
      ))}
    </div>
  );
}

function SortableCategoryChip({
  category,
  onColorChange,
  onNameChange,
  onDelete,
}: {
  category: CategoryItem;
  onColorChange: (id: string, color: TagColorEnum) => void;
  onNameChange: (id: string, name: string) => void;
  onDelete: (id: string) => void;
}) {
  const { colorScheme: mode } = useColorScheme();
  const [isColorPopperOpen, setIsColorPopperOpen] = useState(false);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: category.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      data-testid={`category-row-${category.id}`}
      style={{
        ...style,
        opacity: isDragging ? 0.4 : 1,
        borderRadius: "calc(var(--radius) - 4px)",
        backgroundColor: "var(--background)",
        border: `1px solid ${"var(--border)"}`,
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        padding: `${"4px"} ${"6px"}`,
      }}
    >
      <div
        {...attributes}
        {...listeners}
        tabIndex={-1}
        style={{
          cursor: "grab",
          display: "flex",
          alignItems: "center",
          color:
            "color-mix(in oklab, var(--muted-foreground) 80%, transparent)",
        }}
      >
        <Icon ReactIcon={GripVerticalIcon} size="sm" />
      </div>

      <Popper
        anchor={
          <button
            style={{
              width: 20,
              height: 20,
              borderRadius: "50%",
              border: "1px solid",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 0,
              background: "none",
              flexShrink: 0,
              fontSize: "10px",
              ...TagColors?.[category.color]?.[mode!],
            }}
          >
            A
          </button>
        }
        isOpenState={[isColorPopperOpen, setIsColorPopperOpen]}
        isClickable={true}
        placement="bottom-start"
        floatingStyle={{ width: "fit-content" }}
      >
        <ColorGrid
          selectedColor={category.color}
          onChange={(color) => {
            onColorChange(category.id, color);
            setIsColorPopperOpen(false);
          }}
        />
      </Popper>

      <input
        type="text"
        data-testid={`category-name-input-${category.id}`}
        value={category.name}
        onChange={(e) => onNameChange(category.id, e.target.value)}
        placeholder="Category name..."
        size={Math.max(category.name.length || 12, 12)}
        style={{
          border: "none",
          background: "transparent",
          outline: "none",
          ...(typographyStyles["body-xs"] as React.CSSProperties),
          color: "var(--foreground)",
          width: "auto",
          minWidth: 0,
        }}
      />

      <button
        data-testid={`category-delete-${category.id}`}
        onClick={() => onDelete(category.id)}
        style={{
          border: "none",
          background: "transparent",
          cursor: "pointer",
          padding: "2px",
          borderRadius: "0.25rem",
          color:
            "color-mix(in oklab, var(--muted-foreground) 80%, transparent)",
          display: "flex",
          alignItems: "center",
        }}
      >
        <Icon ReactIcon={Trash2Icon} size="xs" />
      </button>
    </div>
  );
}

function DragOverlayChip({ category }: { category: CategoryItem }) {
  const { colorScheme: mode } = useColorScheme();

  return (
    <div
      style={{
        borderRadius: "calc(var(--radius) - 4px)",
        backgroundColor: "var(--card)",
        border: `1px solid ${"var(--ring)"}`,
        display: "flex",
        alignItems: "center",
        gap: "6px",
        padding: `${"4px"} ${"6px"}`,
        boxShadow: "var(--shadow-md)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          color:
            "color-mix(in oklab, var(--muted-foreground) 80%, transparent)",
        }}
      >
        <Icon ReactIcon={GripVerticalIcon} size="sm" />
      </div>
      <div
        style={{
          width: 20,
          height: 20,
          borderRadius: "50%",
          border: "1px solid",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "10px",
          flexShrink: 0,
          ...TagColors?.[category.color]?.[mode!],
        }}
      >
        A
      </div>
      <span
        style={{
          ...(typographyStyles["body-xs"] as React.CSSProperties),
          color: "var(--foreground)",
        }}
      >
        {category.name || "Unnamed"}
      </span>
    </div>
  );
}

function DroppableCategoryCell({
  groupId,
  categories,
  onColorChange,
  onNameChange,
  onDelete,
  onAddCategory,
}: {
  groupId: string;
  categories: CategoryItem[];
  onColorChange: (id: string, color: TagColorEnum) => void;
  onNameChange: (id: string, name: string) => void;
  onDelete: (id: string) => void;
  onAddCategory: () => void;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: groupId });

  return (
    <div
      ref={setNodeRef}
      className={cn(
        "flex-1 min-h-8 p-1.5",
        categories.length === 0 ? "pt-3" : "pt-1.5",
        "rounded bg-transparent",
      )}
    >
      <SortableContext
        items={categories.map((c) => c.id)}
        strategy={verticalListSortingStrategy}
      >
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "4px",
            alignItems: "center",
          }}
        >
          {categories.map((cat) => (
            <SortableCategoryChip
              key={cat.id}
              category={cat}
              onColorChange={onColorChange}
              onNameChange={onNameChange}
              onDelete={onDelete}
            />
          ))}
          <button
            data-testid={`add-category-${groupId}`}
            onClick={onAddCategory}
            style={{
              border: "none",
              background: "transparent",
              cursor: "pointer",
              padding: "2px",
              borderRadius: "calc(var(--radius) - 4px)",
              display: "inline-flex",
              alignItems: "center",
              gap: "4px",
              color: "var(--foreground)",
              ...(typographyStyles["body-xs"] as React.CSSProperties),
            }}
          >
            <Icon ReactIcon={PlusIcon} size="xs" />
            Add
          </button>
        </div>
      </SortableContext>

      {isOver && categories.length === 0 && (
        <Typography
          level="body-xs"
          className="text-muted-foreground/80 text-center pt-1 pb-1"
        >
          Drop here
        </Typography>
      )}
    </div>
  );
}

function TreeRow({
  node,
  categories,
  isExpanded,
  onToggleExpand,
  onColorChange,
  onNameChange,
  onDelete,
  onAddCategory,
  isLast = false,
}: {
  node: TreeNode;
  categories: CategoryItem[];
  isExpanded: boolean;
  onToggleExpand: () => void;
  onColorChange: (id: string, color: TagColorEnum) => void;
  onNameChange: (id: string, name: string) => void;
  onDelete: (id: string) => void;
  onAddCategory: () => void;
  isLast?: boolean;
}) {
  const hasChildren = node.children.length > 0;
  const displayName = node.account.path
    ? `${node.account.path} ${node.account.name}`
    : node.account.name;

  return (
    <div
      data-testid={`tree-node-${node.account.id}`}
      className={cn(
        "flex",
        !isLast &&
          "[border-bottom-width:1px] [border-bottom-style:solid] border-border",
      )}
    >
      {/* Column 1: Account name */}
      <div
        onClick={hasChildren ? onToggleExpand : undefined}
        className={cn(
          "w-110 min-w-110 min-h-11 flex items-start pt-3 gap-1 pl-(--padding-left) pr-2",
          hasChildren ? "cursor-pointer" : "cursor-default",
          "[user-select:none]",
          hasChildren && "hover:bg-muted",
        )}
        style={
          {
            "--padding-left": `${(1 + node.depth * 1.5) * 8}px`,
          } as CSSProperties
        }
      >
        <div className="w-3.5 min-w-3.5 flex justify-center mt-0.75">
          {hasChildren && (
            <Icon
              ReactIcon={isExpanded ? icons.chevronDown : icons.chevronRight}
              size="xs"
            />
          )}
        </div>
        <Typography
          level="body-md"
          data-testid={`tree-node-name-${node.account.id}`}
          className="overflow-hidden text-ellipsis whitespace-nowrap"
        >
          {displayName}
        </Typography>
      </div>

      {/* Column 2: Categories */}
      <div className="flex-1 [border-left-width:1px] [border-left-style:solid] border-border">
        <DroppableCategoryCell
          groupId={node.account.id}
          categories={categories}
          onColorChange={onColorChange}
          onNameChange={onNameChange}
          onDelete={onDelete}
          onAddCategory={onAddCategory}
        />
      </div>
    </div>
  );
}

export type ManageCategoriesContextType = {
  entityIdentifier?: string;
};

export function Component({
  context,
}: {
  commandInstanceId: string;
  commandDefinitionId: string;
  context?: ManageCategoriesContextType;
}) {
  const { ref: scopeRef } = useContextScope({ capturesInput: true });
  const { scopedActiveEntity, entities } = useScope();
  const resolvedEntity = context?.entityIdentifier
    ? entities?.find((e) => e?.identifier === context.entityIdentifier)
    : (scopedActiveEntity ?? entities?.[0]);
  const entityIdentifier =
    context?.entityIdentifier ?? resolvedEntity?.identifier;

  const { data: accountsData, loading: nodeLoading } = useQuery(
    CHARTED_ACCOUNTS_QUERY,
    {
      variables: {
        entityIdentifiers: entityIdentifier ? [entityIdentifier] : [],
      },
      skip: !entityIdentifier,
      fetchPolicy: "cache-and-network",
    },
  );

  // Its accounts, as the tree they are filed under - its categories apart.
  const everyAccount: ChartedAccountOption[] = useMemo(
    () => accountsData?.chartedAccounts?.data || [],
    [accountsData],
  );
  const chartedAccounts: ChartedAccountOption[] = useMemo(
    () => everyAccount.filter((account) => !account.isCategory),
    [everyAccount],
  );
  // Its categories as they were loaded: what a save is set against.
  const loadedRef = useRef<CategoryItem[]>([]);

  const tree = useMemo(() => buildTree(chartedAccounts), [chartedAccounts]);

  const [categories, setCategories] = useState<CategoryItem[]>([]);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [activeId, setActiveId] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const pendingGroupRef = useRef<string | null>(null);
  const initializedRef = useRef(false);

  useEffect(() => {
    if (tree.length > 0 && expandedIds.size === 0) {
      setExpandedIds(new Set(tree.map((n) => n.account.id)));
    }
  }, [tree]);

  useEffect(() => {
    if (accountsData?.chartedAccounts && !initializedRef.current) {
      const cats: CategoryItem[] = everyAccount
        .filter((account) => account.isCategory)
        .map((account, index) => ({
          id: account.id,
          identifier: account.identifier,
          name: account.name || "",
          color: account.color || TagColorEnum.Grey,
          position: index,
          chartedAccountId: account.parent?.id || null,
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
      setCategories(cats);
      loadedRef.current = cats;
      initializedRef.current = true;
    }
  }, [accountsData, everyAccount]);

  const categoriesByAccount = useMemo(() => {
    const groups: Record<string, CategoryItem[]> = { [UNASSIGNED_GROUP]: [] };
    for (const acct of chartedAccounts) {
      groups[acct.id] = [];
    }
    for (const cat of categories) {
      const key = cat.chartedAccountId || UNASSIGNED_GROUP;
      if (!groups[key]) groups[key] = [];
      groups[key].push(cat);
    }
    return groups;
  }, [categories, chartedAccounts]);

  const visibleNodes = useMemo(
    () => flattenVisibleTree(tree, expandedIds),
    [tree, expandedIds],
  );

  const allGroupIds = useMemo(() => {
    const ids = [UNASSIGNED_GROUP];
    for (const acct of chartedAccounts) {
      ids.push(acct.id);
    }
    return ids;
  }, [chartedAccounts]);

  const findGroupForCategory = useCallback(
    (categoryId: string): string | null => {
      for (const [groupId, cats] of Object.entries(categoriesByAccount)) {
        if (cats.some((c) => c.id === categoryId)) return groupId;
      }
      return null;
    },
    [categoriesByAccount],
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  const collisionDetection: CollisionDetection = useCallback(
    (args) => {
      const droppableContainers = args.droppableContainers.filter((c) =>
        allGroupIds.includes(String(c.id)),
      );
      const containerHits = rectIntersection({
        ...args,
        droppableContainers,
      });
      if (containerHits.length > 0) {
        const matchedContainerId = String(containerHits[0].id);
        const containerCatIds = new Set(
          (categoriesByAccount[matchedContainerId] ?? []).map((c) => c.id),
        );
        const itemsInContainer = args.droppableContainers.filter((c) =>
          containerCatIds.has(String(c.id)),
        );
        if (itemsInContainer.length > 0) {
          const itemHits = closestCenter({
            ...args,
            droppableContainers: itemsInContainer,
          });
          if (itemHits.length > 0) {
            return itemHits;
          }
        }
        return containerHits;
      }
      return closestCenter(args);
    },
    [allGroupIds, categoriesByAccount],
  );

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(String(event.active.id));
  }, []);

  const handleDragOver = useCallback(
    (event: DragOverEvent) => {
      const { active, over } = event;
      if (!over) return;

      const activeGroup = findGroupForCategory(String(active.id));
      let overGroup = findGroupForCategory(String(over.id));

      if (!overGroup && allGroupIds.includes(String(over.id))) {
        overGroup = String(over.id);
      }

      if (!activeGroup || !overGroup || activeGroup === overGroup) return;

      pendingGroupRef.current = overGroup;
    },
    [findGroupForCategory, allGroupIds],
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const pendingGroup = pendingGroupRef.current;
      pendingGroupRef.current = null;
      setActiveId(null);
      const { active, over } = event;

      const applyPendingMove = () => {
        if (pendingGroup) {
          const activeGroup = findGroupForCategory(String(active.id));
          if (activeGroup !== pendingGroup) {
            setCategories((prev) =>
              prev.map((cat) =>
                cat.id === String(active.id)
                  ? {
                      ...cat,
                      chartedAccountId:
                        pendingGroup === UNASSIGNED_GROUP ? null : pendingGroup,
                    }
                  : cat,
              ),
            );
            setHasChanges(true);
          }
        }
      };

      if (!over || active.id === over.id) {
        applyPendingMove();
        return;
      }

      // Cross-container: over is a group droppable
      if (allGroupIds.includes(String(over.id))) {
        const targetGroup = String(over.id);
        setCategories((prev) =>
          prev.map((cat) =>
            cat.id === String(active.id)
              ? {
                  ...cat,
                  chartedAccountId:
                    targetGroup === UNASSIGNED_GROUP ? null : targetGroup,
                }
              : cat,
          ),
        );
        setHasChanges(true);
        return;
      }

      const activeGroup = findGroupForCategory(String(active.id));
      const overGroup = findGroupForCategory(String(over.id));

      // Cross-container: over is a category in a different group
      if (activeGroup && overGroup && activeGroup !== overGroup) {
        setCategories((prev) =>
          prev.map((cat) =>
            cat.id === String(active.id)
              ? {
                  ...cat,
                  chartedAccountId:
                    overGroup === UNASSIGNED_GROUP ? null : overGroup,
                }
              : cat,
          ),
        );
        setHasChanges(true);
        return;
      }

      // Same-container reorder
      if (activeGroup && overGroup && activeGroup === overGroup) {
        const groupCats = categoriesByAccount[activeGroup] || [];
        const oldIndex = groupCats.findIndex((c) => c.id === String(active.id));
        const newIndex = groupCats.findIndex((c) => c.id === String(over.id));
        if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
          const reordered = arrayMove(groupCats, oldIndex, newIndex);
          const reorderedIds = reordered.map((c) => c.id);
          setCategories((prev) => {
            const otherCats = prev.filter((c) => !reorderedIds.includes(c.id));
            const thisGroupCats = reorderedIds.map(
              (id) => prev.find((c) => c.id === id)!,
            );
            return [...otherCats, ...thisGroupCats];
          });
          setHasChanges(true);
          return;
        }
      }

      applyPendingMove();
    },
    [allGroupIds, findGroupForCategory, categoriesByAccount],
  );

  const handleColorChange = useCallback((id: string, color: TagColorEnum) => {
    setCategories((prev) =>
      prev.map((c) => (c.id === id ? { ...c, color } : c)),
    );
    setHasChanges(true);
  }, []);

  const handleNameChange = useCallback((id: string, name: string) => {
    setCategories((prev) =>
      prev.map((c) => (c.id === id ? { ...c, name } : c)),
    );
    setHasChanges(true);
  }, []);

  const handleDelete = useCallback((id: string) => {
    setCategories((prev) => prev.filter((c) => c.id !== id));
    setHasChanges(true);
  }, []);

  const handleAddCategory = useCallback(
    (chartedAccountId: string | null) => {
      const availableColors = Object.values(TagColorEnum);
      const nextColor =
        availableColors[categories.length % availableColors.length];
      setCategories((prev) => [
        ...prev,
        {
          id: `temp_${Date.now()}`,
          identifier: null,
          name: "",
          color: nextColor,
          position: prev.length,
          chartedAccountId,
        },
      ]);
      setHasChanges(true);
    },
    [categories.length],
  );

  const handleToggleExpand = useCallback((accountId: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(accountId)) next.delete(accountId);
      else next.add(accountId);
      return next;
    });
  }, []);

  const [createNode, { loading: creating }] = useMutation(
    Mutations.CREATE_NODE,
  );
  const [updateNode, { loading: updating }] = useMutation(
    Mutations.UPDATE_NODE,
  );
  const [deleteNodes, { loading: deleting }] = useMutation(
    Mutations.DELETE_NODES,
  );
  const saving = creating || updating || deleting;

  // Each category an account under the one it narrows: those added made, those
  // changed - renamed, recoloured, moved - updated, those taken away deleted.
  const handleSave = useCallback(async () => {
    if (!entityIdentifier) return;
    const loaded = new Map(loadedRef.current.map((cat) => [cat.id, cat]));
    const kept = new Set(categories.map((cat) => cat.id));
    const gone = loadedRef.current.filter((cat) => !kept.has(cat.id));
    await Promise.all([
      ...categories
        .filter((cat) => cat.name.trim() && cat.chartedAccountId)
        .map((cat) => {
          const was = loaded.get(cat.id);
          const parent = { identifier: idToIdentifier(cat.chartedAccountId!) };
          if (!was) {
            return createNode({
              variables: {
                input: {
                  entityIdentifier,
                  node: {
                    chartedAccount: {
                      name: cat.name.trim(),
                      color: cat.color,
                      isCategory: true,
                      parent,
                    },
                  },
                },
              },
            });
          }
          if (
            was.name === cat.name &&
            was.color === cat.color &&
            was.chartedAccountId === cat.chartedAccountId
          ) {
            return null;
          }
          return updateNode({
            variables: {
              input: {
                id: cat.id,
                node: {
                  chartedAccount: {
                    name: cat.name.trim(),
                    color: cat.color,
                    parent,
                  },
                },
              },
            },
          });
        }),
      ...(gone.length > 0
        ? [
            deleteNodes({
              variables: { input: { ids: gone.map((cat) => cat.id) } },
            }),
          ]
        : []),
    ]);

    setHasChanges(false);
    State.getState().popCommandsFromStack();
  }, [entityIdentifier, categories, createNode, updateNode, deleteNodes]);

  const setCommandCenterEnterHandler = State(
    (state) => state.setCommandCenterEnterHandler,
  );

  useEffect(() => {
    const handler = hasChanges && !saving ? handleSave : null;
    setCommandCenterEnterHandler(handler);
    return () => setCommandCenterEnterHandler(null);
  }, [hasChanges, saving, handleSave, setCommandCenterEnterHandler]);

  const activeCategory = activeId
    ? categories.find((c) => c.id === activeId)
    : null;

  if (nodeLoading) {
    return (
      <div
        data-testid="manage-categories-panel"
        className="p-4 flex flex-col gap-2"
      >
        {Array.from({ length: 5 }, (_, index) => (
          <Skeleton
            key={index}
            variant="rectangular"
            height={32}
            className="rounded-sm"
          />
        ))}
      </div>
    );
  }

  return (
    <div
      ref={scopeRef}
      data-testid="manage-categories-panel"
      className="w-full h-full flex flex-col"
    >
      <DndContext
        sensors={sensors}
        collisionDetection={collisionDetection}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
      >
        <div className="flex-1 overflow-auto p-4">
          <div className="[border-width:1px] [border-style:solid] border-border rounded-sm overflow-hidden">
            {/* Column headers */}
            <div className="flex [border-bottom-width:1px] [border-bottom-style:solid] border-border bg-muted">
              <div className="w-110 min-w-110 pl-3 pr-3 pt-1 pb-1">
                <Typography
                  level="body-xs"
                  className="uppercase text-muted-foreground/80"
                >
                  Account
                </Typography>
              </div>
              <div className="flex-1 pl-2 pr-2 pt-1 pb-1 [border-left-width:1px] [border-left-style:solid] border-border">
                <Typography
                  level="body-xs"
                  className="uppercase text-muted-foreground/80"
                >
                  Categories
                </Typography>
              </div>
            </div>

            {/* Tree rows */}
            <div data-testid="charted-accounts-tree">
              {visibleNodes.map((node, i) => (
                <TreeRow
                  key={node.account.id}
                  node={node}
                  categories={categoriesByAccount[node.account.id] || []}
                  isExpanded={expandedIds.has(node.account.id)}
                  onToggleExpand={() => handleToggleExpand(node.account.id)}
                  onColorChange={handleColorChange}
                  onNameChange={handleNameChange}
                  onDelete={handleDelete}
                  onAddCategory={() => handleAddCategory(node.account.id)}
                  isLast={i === visibleNodes.length - 1}
                />
              ))}
            </div>
          </div>
        </div>

        <DragOverlay>
          {activeCategory ? (
            <DragOverlayChip category={activeCategory} />
          ) : null}
        </DragOverlay>
      </DndContext>
    </div>
  );
}

function Header() {
  return (
    <div className="flex items-center gap-2 w-full">
      <Icon ReactIcon={TagsIcon} />
      <Typography level="title-md" className="flex-1">
        Manage Categories
      </Typography>
    </div>
  );
}

function Footer() {
  const commandCenterEnterHandler = State(
    (state) => state.commandCenterEnterHandler,
  );

  return (
    <CommandCenterListFooter
      buttons={[
        {
          variant: "default",
          size: "sm",
          children: "Save",
          "data-testid": "manage-categories-save",
          onClick: () => commandCenterEnterHandler?.(),
          disabled: !commandCenterEnterHandler,
          hotkey: "enter",
        },
      ]}
    />
  );
}

export function ManageCategoriesCommand() {
  const registerCommandDefinition = State(
    (state) => state.registerCommandDefinition,
  );
  const unregisterCommandDefinition = State(
    (state) => state.unregisterCommandDefinition,
  );

  useLayoutEffect(() => {
    registerCommandDefinition({
      id: "manage-categories",
      title: "Manage Categories",
      description: "Organize categories by charted account",
      component: Component,
      header: Header,
      footer: Footer,
      ReactIcon: TagsIcon,
      isResizable: true,
      sizes: ["sm", "md", "lg"],
      onClick: () => {
        State.getState().pushToCommandStack({
          commandDefinitionId: "manage-categories",
        });
      },
    });

    return () => {
      unregisterCommandDefinition("manage-categories");
    };
  }, []);

  return null;
}
